import {
  BaseMessageSignerWalletAdapter,
  WalletAdapterNetwork,
  WalletConnectionError,
  WalletDisconnectedError,
  WalletDisconnectionError,
  WalletName,
  WalletNotConnectedError,
  WalletNotReadyError,
  WalletReadyState,
  WalletSignMessageError,
  WalletSignTransactionError,
} from '@solana/wallet-adapter-base'
import { PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js'

interface BackpackWallet {
  isBackpack?: boolean
  publicKey?: { toBytes(): Uint8Array }
  isConnected: boolean
  signTransaction<T extends Transaction | VersionedTransaction>(transaction: T): Promise<T>
  signAllTransactions<T extends Transaction | VersionedTransaction>(transactions: T[]): Promise<T[]>
  signMessage(message: Uint8Array): Promise<{ signature: Uint8Array }>
  connect(): Promise<void>
  disconnect(): Promise<void>
  on(event: string, handler: (args: any) => void): void
  off(event: string, handler: (args: any) => void): void
}

interface BackpackWindow extends Window {
  backpack?: {
    solana?: BackpackWallet
  }
}

declare const window: BackpackWindow

export interface BackpackWalletAdapterConfig {}

export const BackpackWalletName = 'Backpack' as WalletName<'Backpack'>

export class BackpackWalletAdapter extends BaseMessageSignerWalletAdapter {
  name = BackpackWalletName
  url = 'https://backpack.app'
  icon = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjgiIGhlaWdodD0iMTI4IiByeD0iMjQiIGZpbGw9InVybCgjcGFpbnQwX2xpbmVhcl8yMl8yKSIvPgo8cGF0aCBkPSJNMzIgNjRDMzIgNDYuMzI3MyA0Ni4zMjczIDMyIDY0IDMyQzgxLjY3MjcgMzIgOTYgNDYuMzI3MyA5NiA2NEM5NiA4MS42NzI3IDgxLjY3MjcgOTYgNjQgOTZDNDYuMzI3MyA5NiAzMiA4MS42NzI3IDMyIDY0WiIgZmlsbD0iIzFFMUUxRSIvPgo8cGF0aCBkPSJNNDggNjRDNDggNTUuMTYzNCA1NS4xNjM0IDQ4IDY0IDQ4QzcyLjgzNjYgNDggODAgNTUuMTYzNCA4MCA2NEM4MCA3Mi44MzY2IDcyLjgzNjYgODAgNjQgODBDNTUuMTYzNCA4MCA0OCA3Mi44MzY2IDQ4IDY0WiIgZmlsbD0id2hpdGUiLz4KPGRlZnM+CjxsaW5lYXJHcmFkaWVudCBpZD0icGFpbnQwX2xpbmVhcl8yMl8yIiB4MT0iMCIgeTE9IjAiIHgyPSIxMjgiIHkyPSIxMjgiIGdyYWRpZW50VW5pdHM9InVzZXJTcGFjZU9uVXNlIj4KPHN0b3Agc3RvcC1jb2xvcj0iIzZGNjhGRSIvPgo8c3RvcCBvZmZzZXQ9IjEiIHN0b3AtY29sb3I9IiM2RjY4RkUiLz4KPC9saW5lYXJHcmFkaWVudD4KPC9kZWZzPgo8L3N2Zz4K'
  readonly supportedTransactionVersions = null

  private _connecting: boolean
  private _wallet: BackpackWallet | null
  private _publicKey: PublicKey | null
  private _readyState: WalletReadyState =
    typeof window === 'undefined'
      ? WalletReadyState.Unsupported
      : WalletReadyState.Loadable

  constructor(config: BackpackWalletAdapterConfig = {}) {
    super()
    this._connecting = false
    this._wallet = null
    this._publicKey = null

    // Check if we're in a browser environment
    if (typeof window !== 'undefined') {
      // Check for desktop browser extension
      if (typeof window.backpack?.solana?.isBackpack === 'boolean') {
        this._readyState = WalletReadyState.Installed
      } else {
        // On mobile or without extension, set as Loadable so it appears in the list
        // The CustomWalletModal will handle mobile deep linking
        this._readyState = WalletReadyState.Loadable
      }
    }
  }

  get publicKey() {
    return this._publicKey
  }

  get connecting() {
    return this._connecting
  }

  get connected() {
    return !!this._wallet?.isConnected
  }

  get readyState() {
    return this._readyState
  }

  async connect(): Promise<void> {
    try {
      if (this.connected || this.connecting) return
      if (this._readyState !== WalletReadyState.Installed) throw new WalletNotReadyError()

      this._connecting = true

      const wallet = window.backpack?.solana
      if (!wallet) throw new WalletNotReadyError()

      try {
        await wallet.connect()
        if (!wallet.publicKey) throw new WalletConnectionError()

        this._wallet = wallet
        this._publicKey = new PublicKey(wallet.publicKey.toBytes())

        this.emit('connect', this._publicKey)
      } catch (error: any) {
        throw new WalletConnectionError(error?.message, error)
      }
    } catch (error: any) {
      this.emit('error', error)
      throw error
    } finally {
      this._connecting = false
    }
  }

  async disconnect(): Promise<void> {
    const wallet = this._wallet
    if (wallet) {
      this._wallet = null
      this._publicKey = null

      try {
        await wallet.disconnect()
      } catch (error: any) {
        this.emit('error', new WalletDisconnectionError(error?.message, error))
      }
    }

    this.emit('disconnect')
  }

  async signTransaction<T extends Transaction | VersionedTransaction>(transaction: T): Promise<T> {
    try {
      const wallet = this._wallet
      if (!wallet) throw new WalletNotConnectedError()

      try {
        return await wallet.signTransaction(transaction)
      } catch (error: any) {
        throw new WalletSignTransactionError(error?.message, error)
      }
    } catch (error: any) {
      this.emit('error', error)
      throw error
    }
  }

  async signAllTransactions<T extends Transaction | VersionedTransaction>(transactions: T[]): Promise<T[]> {
    try {
      const wallet = this._wallet
      if (!wallet) throw new WalletNotConnectedError()

      try {
        return await wallet.signAllTransactions(transactions)
      } catch (error: any) {
        throw new WalletSignTransactionError(error?.message, error)
      }
    } catch (error: any) {
      this.emit('error', error)
      throw error
    }
  }

  async signMessage(message: Uint8Array): Promise<Uint8Array> {
    try {
      const wallet = this._wallet
      if (!wallet) throw new WalletNotConnectedError()

      try {
        const { signature } = await wallet.signMessage(message)
        return signature
      } catch (error: any) {
        throw new WalletSignMessageError(error?.message, error)
      }
    } catch (error: any) {
      this.emit('error', error)
      throw error
    }
  }
}